import { ref, markRaw, type Ref, type Component } from "vue";
import type { AntlerModule } from "../antler-provider";
import { ModalSize } from "@/Components/Shared/modal";

export interface ModalOptions {
    component?: Component;
    props?: Record<string, unknown>;
    content?: string;
    title?: string;
    headerContent?: string;
    footerContent?: string;
    alert?: string;
    size?: ModalSize;
    notClosable?: boolean;
    hideCloseButton?: boolean;
    onClose?: () => void;
    onConfirm?: () => void;
}

export interface Modal extends ModalOptions {
    id: string;
    isOpen: boolean;
}

export interface ModalModule extends AntlerModule {
    modals: Ref<Modal[]>;
    openModal: (options: ModalOptions) => string;
    closeModal: (id: string) => void;
    closeAllModals: () => void;
    getModal: (id: string) => Modal | undefined;
    updateModal: (id: string, options: Partial<ModalOptions>) => void;
}

export const createModalModule = (): ModalModule => {
    const modals = ref<Modal[]>([]);
    let modalIdCounter = 0;

    const generateModalId = (): string => {
        modalIdCounter++;
        return `modal-${modalIdCounter}`;
    };

    const openModal = (options: ModalOptions): string => {
        const id = generateModalId();
        const modal: Modal = {
            id,
            isOpen: true,
            ...options,
            component: options.component
                ? markRaw(options.component)
                : undefined,
        };
        modals.value.push(modal);
        return id;
    };

    const closeModal = (id: string): void => {
        const modalIndex = modals.value.findIndex(m => m.id === id);
        if (modalIndex !== -1) {
            const modal = modals.value[modalIndex];
            modal.onClose?.();
            modals.value.splice(modalIndex, 1);
        }
    };

    const closeAllModals = (): void => {
        modals.value.forEach(modal => {
            modal.onClose?.();
        });
        modals.value = [];
    };

    const getModal = (id: string): Modal | undefined => {
        return modals.value.find(m => m.id === id);
    };

    const updateModal = (id: string, options: Partial<ModalOptions>): void => {
        const modal = modals.value.find(m => m.id === id);
        if (modal) {
            Object.assign(modal, options);
            if (options.component) {
                modal.component = markRaw(options.component);
            }
        }
    };

    return {
        id: "modal",
        name: "Modal Module",
        version: "1.0.0",

        modals,
        openModal,
        closeModal,
        closeAllModals,
        getModal,
        updateModal,

        init() {
            // Module initialized
        },

        destroy() {
            closeAllModals();
        },
    };
};
